import { useMemo, useCallback } from 'react';
import { pick } from 'lodash/fp';

import {
  useLabelSuggestionsLazyQuery,
  LabelSuggestionsQuery,
  LabelValue,
  LabelSuggestionsInput
} from 'graphql/types';
import { useFeatureFlag } from '../../common/hooks';
import { useIsSmartSuggestionSettingEnabled } from './useIsSmartSuggestionSettingEnabled';

export type SuggestedLabelValue = {
  labelValue: [LabelValue | null] | undefined;
  metadata?: string;
  thresholdMet?: boolean;
};

export type LabelSuggestionType = {
  labelSuggestionData: LabelSuggestionsQuery | undefined;
  getLabelSuggestions: (input: LabelSuggestionsInput | undefined) => void;
  getSuggestions: (
    suggestions: LabelSuggestionsQuery | undefined,
    type: string | undefined
  ) => SuggestedLabelValue;
  loading: boolean;
};

export const useLabelSuggestion = (): LabelSuggestionType => {
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const isEmptyLabelsEnabled = useFeatureFlag('isEmptyLabelsEnabled', false);
  const [
    queryLabelSuggestions,
    { data: labelSuggestionData, loading }
  ] = useLabelSuggestionsLazyQuery();

  const getLabelSuggestions = useCallback(
    (input: LabelSuggestionsInput | undefined) => {
      const variables: { input: LabelSuggestionsInput } = {
        input: {
          date: input?.date,
          startTime: input?.startTime,
          endTime: input?.endTime,
          duration: input?.duration,
          description: input?.description,
          inferenceId: input?.inferenceId,
          labelValues: input ? input.labelValues : [],
          attributes: input?.attributes,
          comment: input?.comment
        }
      };

      queryLabelSuggestions({ variables });
    },
    [queryLabelSuggestions]
  );

  const getSuggestions = useCallback(
    (
      suggestions: LabelSuggestionsQuery | undefined,
      labelId: string | undefined
    ): SuggestedLabelValue => {
      const labSuggesion = suggestions?.labelSuggestions?.suggestions?.filter(
        (x) => x?.label?.id === labelId
      );

      const picker = (labelValue: unknown) =>
        isEmptyLabelsEnabled && !labelValue
          ? null
          : pick(['idValue', 'numericValue', 'stringValue'])(labelValue);
      const values = labSuggesion?.map((y) => y?.suggestedValues);
      const metadata = labSuggesion?.map((s) => s?.suggestionMetadata);
      const valueArray = values ? values[0] : null;
      const labelValue =
        valueArray && Array.isArray(valueArray)
          ? (valueArray.map(picker) as [LabelValue | null])
          : undefined;
      const thresholdMet = labSuggesion?.[0]?.thresholdMet;
      return {
        labelValue,
        metadata: (metadata && metadata[0]) || undefined,
        thresholdMet: thresholdMet
      };
    },
    [isEmptyLabelsEnabled]
  );

  return useMemo(
    () => ({
      getLabelSuggestions: isSmartSuggestionEnabled
        ? getLabelSuggestions
        : () => {},
      labelSuggestionData: isSmartSuggestionEnabled
        ? labelSuggestionData
        : undefined,
      getSuggestions: getSuggestions,
      loading
    }),
    [
      isSmartSuggestionEnabled,
      getSuggestions,
      getLabelSuggestions,
      labelSuggestionData,
      loading
    ]
  );
};
